/**
 * Event ID Manager for Snapchat Conversion Tracking
 * Manages deduplication IDs for Pixel SDK and CAPI
 *
 * According to Snapchat documentation:
 * - client_dedup_id: Universal deduplication key for ALL event types
 * - MUST be unique for EVERY event (not shared between different events)
 * - Should be the same value sent from BOTH Pixel SDK and CAPI for the SAME event
 * - Used to prevent the SAME event from being counted twice when fired from both sources
 * - 48-hour deduplication window
 *
 * Critical: Do NOT reuse IDs across different events (e.g., InitiateCheckout and Purchase).
 * Each event occurrence must have its own unique client_dedup_id.
 */

interface EventIdStore {
  [eventType: string]: {
    id: string
    timestamp: number
  }
}

const EVENT_ID_STORAGE_KEY = 'snapchat_event_ids'
const EVENT_ID_TTL = 30 * 60 * 1000 // 30 minutes

/**
 * Generate a unique event ID
 */
function generateEventId(): string {
  return crypto?.randomUUID?.() ?? ('snap_' + Date.now() + '_' + Math.random().toString(36).slice(2))
}

/**
 * Get stored event IDs from sessionStorage
 */
function getStoredEventIds(): EventIdStore {
  try {
    const stored = sessionStorage.getItem(EVENT_ID_STORAGE_KEY)
    if (!stored) return {}

    const parsed = JSON.parse(stored) as EventIdStore
    const now = Date.now()

    // Clean up expired event IDs
    const cleaned: EventIdStore = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (now - value.timestamp < EVENT_ID_TTL) {
        cleaned[key] = value
      }
    }

    return cleaned
  } catch (error) {
    return {}
  }
}

/**
 * Store event ID in sessionStorage
 */
function storeEventId(eventType: string, eventId: string): void {
  try {
    const stored = getStoredEventIds()
    stored[eventType] = {
      id: eventId,
      timestamp: Date.now()
    }
    sessionStorage.setItem(EVENT_ID_STORAGE_KEY, JSON.stringify(stored))
  } catch (error) {
    // Silent fail
  }
}

/**
 * Get or create an event ID for a specific event type
 * @param eventType - The type of event (e.g., 'initiate_checkout', 'purchase', 'page_view')
 * @param forceNew - Force generation of a new event ID even if one exists
 * @returns Event ID string
 */
export function getEventId(eventType: string, forceNew: boolean = false): string {
  const stored = getStoredEventIds()

  // If we have a stored ID and not forcing new, return it
  if (!forceNew && stored[eventType]) {
    return stored[eventType].id
  }

  // Generate new event ID
  const eventId = generateEventId()
  storeEventId(eventType, eventId)

  return eventId
}

/**
 * Get event ID for InitiateCheckout (START_CHECKOUT in Snapchat)
 * Generates a new ID each time checkout is initiated
 */
export function getInitiateCheckoutEventId(): string {
  return getEventId('initiate_checkout', true) // Always generate new for checkout
}

/**
 * Get event ID for Purchase
 * IMPORTANT: Always generates a NEW unique ID per Snapchat's requirements
 * "The client_dedup_id can be any string, as long as it is unique for every event"
 *
 * Each Purchase event must have its own unique ID to properly deduplicate
 * between Pixel and CAPI, but should NOT reuse IDs from other event types.
 */
export function getPurchaseEventId(): string {
  // Always generate new purchase ID - each event must have unique client_dedup_id
  return getEventId('purchase', true)
}

/**
 * Get event ID for Page View
 * Generates a new ID for each page view
 */
export function getPageViewEventId(): string {
  return getEventId('page_view', true) // Always generate new for page views
}

/**
 * Clear all stored event IDs (useful for testing)
 */
export function clearEventIds(): void {
  try {
    sessionStorage.removeItem(EVENT_ID_STORAGE_KEY)
  } catch (error) {
    // Silent fail
  }
}

/**
 * Get an event ID from Stripe session metadata if available
 * This is used to maintain consistency between frontend and backend tracking
 */
export function getEventIdFromSession(sessionId: string | null): string | null {
  if (!sessionId) return null

  // The event_id will be fetched from the backend /session-details endpoint
  // This function is a placeholder for that integration
  return null
}
